import {
  ErrorNote,
  PageHeader,
  PagedTableShell,
  Stat,
  Table,
  Td,
  useClientPaging,
} from '../components/ui/index.js';
import { useRemote } from '../lib/remote.js';
import { useAdminLocale } from '../lib/admin-locale.js';

interface CostSlice {
  calls: number;
  inputTokens: number;
  outputTokens: number;
  estimatedCost: string;
  currency: 'USD';
  uncertainCalls: number;
}

interface CostReport {
  period: { startsAt: string; endsAt: string; timezone: string };
  totals: CostSlice;
  models: Array<CostSlice & { model: string }>;
  operations: Array<CostSlice & { operation: string }>;
  tenants: Array<
    CostSlice & {
      tenantId: string;
      name: string;
      sites: Array<CostSlice & { siteId: string; name: string; origin: string; deleted: boolean }>;
    }
  >;
}

const OPERATIONS: Record<string, { el: string; en: string }> = {
  answer: { el: 'Απαντήσεις', en: 'Answers' },
  embed: { el: 'Embeddings ερωτήσεων', en: 'Question embeddings' },
  ingest_embed: { el: 'Embeddings περιεχομένου', en: 'Content embeddings' },
};

function countLabel(value: number, locale: string): string {
  return new Intl.NumberFormat(locale).format(value);
}

function money(amount: string, locale: string): string {
  const value = Number(amount);
  if (!Number.isFinite(value)) return '—';
  const digits = value !== 0 && Math.abs(value) < 0.01 ? 6 : 2;
  return `${new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: digits,
  }).format(value)} USD`;
}

function costLabel(
  slice: Pick<CostSlice, 'estimatedCost' | 'uncertainCalls'>,
  locale: string,
  unknown: string,
): string {
  const value = Number(slice.estimatedCost);
  if (slice.uncertainCalls > 0 && value === 0) return unknown;
  return money(slice.estimatedCost, locale);
}

function hostOf(origin: string): string {
  try {
    return new URL(origin).host;
  } catch {
    return origin || '—';
  }
}

function operationLabel(value: string, text: (el: string, en: string) => string): string {
  const label = OPERATIONS[value];
  return label ? text(label.el, label.en) : value;
}

function monthLabel(iso: string, locale: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(locale, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

export function OpenAiCostPage() {
  const { locale, text } = useAdminLocale();
  const intlLocale = locale === 'en' ? 'en-GB' : 'el-GR';
  const cost = (slice: Pick<CostSlice, 'estimatedCost' | 'uncertainCalls'>) =>
    costLabel(slice, intlLocale, text('Άγνωστο', 'Unknown'));
  const remote = useRemote<CostReport>('/v1/platform/costs', null);
  const report = remote.data;
  const models = report?.models ?? [];
  const operations = report?.operations ?? [];
  const tenants = report?.tenants ?? [];
  const sites = tenants.flatMap((tenant) => tenant.sites.map((site) => ({ tenant, site })));
  const modelsPaging = useClientPaging(models);
  const operationsPaging = useClientPaging(operations);
  const tenantsPaging = useClientPaging(tenants);
  const sitesPaging = useClientPaging(sites);

  return (
    <section className="space-y-6">
      <PageHeader
        description={text(
          'Εκτίμηση από τα tokens που καταγράφηκαν αυτόν τον μήνα (UTC). Δεν είναι το τιμολόγιο του OpenAI. Οι κλήσεις mock δεν μετρούν.',
          'Estimate based on tokens recorded this month (UTC). This is not the OpenAI invoice. Mock calls are excluded.',
        )}
      />
      {remote.error ? <ErrorNote message={remote.error.message} /> : null}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          icon="chart"
          accent
          label={text('Εκτίμηση μήνα', 'Monthly estimate')}
          value={report ? cost(report.totals) : '—'}
          hint={
            report
              ? `${monthLabel(report.period.startsAt, intlLocale)} · ${report.totals.uncertainCalls > 0 ? text(`${countLabel(report.totals.uncertainCalls, intlLocale)} κλήσεις χωρίς γνωστή χρέωση`, `${countLabel(report.totals.uncertainCalls, intlLocale)} calls with unknown pricing`) : text('Χωρίς άγνωστες χρεώσεις', 'No unknown charges')}`
              : text('Φόρτωση', 'Loading')
          }
        />
        <Stat
          icon="chat"
          label={text('Κλήσεις', 'Calls')}
          value={countLabel(report?.totals.calls ?? 0, intlLocale)}
          hint={text(
            'Απαντήσεις και embeddings προς το OpenAI',
            'Answers and embeddings sent to OpenAI',
          )}
        />
        <Stat
          icon="arrow"
          label={text('Tokens εισόδου', 'Input tokens')}
          value={countLabel(report?.totals.inputTokens ?? 0, intlLocale)}
          hint={text('Prompt και κείμενο προς embedding', 'Prompt and text sent for embedding')}
        />
        <Stat
          icon="layers"
          label={text('Tokens εξόδου', 'Output tokens')}
          value={countLabel(report?.totals.outputTokens ?? 0, intlLocale)}
          hint={text('Απαντήσεις του μοντέλου', 'Model answers')}
        />
      </div>
      <div className="grid items-stretch gap-4 lg:grid-cols-2">
        <section className="flex flex-col overflow-hidden admin-surface">
          <div className="shrink-0 border-b border-slate-100 px-5 py-4 dark:border-slate-800">
            <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              {text('Ανά μοντέλο', 'By model')}
            </h2>
          </div>
          <PagedTableShell pagination={modelsPaging.total > 0 ? modelsPaging.pager : null}>
            <Table
              head={[
                text('Μοντέλο', 'Model'),
                text('Κλήσεις', 'Calls'),
                text('Εκτίμηση', 'Estimate'),
              ]}
            >
              {modelsPaging.rows.map((row) => (
                <tr key={row.model}>
                  <Td>{row.model}</Td>
                  <Td>{countLabel(row.calls, intlLocale)}</Td>
                  <Td>{cost(row)}</Td>
                </tr>
              ))}
            </Table>
          </PagedTableShell>
          {report && report.models.length === 0 ? (
            <p className="px-5 py-4 text-sm text-slate-500 dark:text-slate-400">
              {text('Καμία κλήση OpenAI αυτόν τον μήνα.', 'No OpenAI calls this month.')}
            </p>
          ) : null}
        </section>
        <section className="flex flex-col overflow-hidden admin-surface">
          <div className="shrink-0 border-b border-slate-100 px-5 py-4 dark:border-slate-800">
            <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              {text('Ανά εργασία', 'By operation')}
            </h2>
          </div>
          <PagedTableShell pagination={operationsPaging.total > 0 ? operationsPaging.pager : null}>
            <Table
              head={[
                text('Εργασία', 'Operation'),
                text('Κλήσεις', 'Calls'),
                text('Εκτίμηση', 'Estimate'),
              ]}
            >
              {operationsPaging.rows.map((row) => (
                <tr key={row.operation}>
                  <Td>{operationLabel(row.operation, text)}</Td>
                  <Td>{countLabel(row.calls, intlLocale)}</Td>
                  <Td>{cost(row)}</Td>
                </tr>
              ))}
            </Table>
          </PagedTableShell>
          {report && report.operations.length === 0 ? (
            <p className="px-5 py-4 text-sm text-slate-500 dark:text-slate-400">
              {text('Καμία κλήση OpenAI αυτόν τον μήνα.', 'No OpenAI calls this month.')}
            </p>
          ) : null}
        </section>
      </div>
      <section className="flex flex-col overflow-hidden admin-surface">
        <div className="shrink-0 border-b border-slate-100 px-5 py-4 dark:border-slate-800">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            {text('Ανά πελάτη', 'By customer')}
          </h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {text(
              'Σύνολο OpenAI του μήνα για κάθε οργανισμό.',
              'Monthly OpenAI total for each organization.',
            )}
          </p>
        </div>
        <PagedTableShell pagination={tenantsPaging.total > 0 ? tenantsPaging.pager : null}>
          <Table
            head={[
              text('Πελάτης', 'Customer'),
              text('Κλήσεις', 'Calls'),
              text('Tokens εισόδου', 'Input tokens'),
              text('Tokens εξόδου', 'Output tokens'),
              text('Εκτίμηση', 'Estimate'),
            ]}
          >
            {tenantsPaging.rows.map((tenant) => (
              <tr key={tenant.tenantId}>
                <Td>
                  <span className="font-medium text-slate-900 dark:text-slate-100">
                    {tenant.name}
                  </span>
                </Td>
                <Td>{countLabel(tenant.calls, intlLocale)}</Td>
                <Td>{countLabel(tenant.inputTokens, intlLocale)}</Td>
                <Td>{countLabel(tenant.outputTokens, intlLocale)}</Td>
                <Td>{cost(tenant)}</Td>
              </tr>
            ))}
          </Table>
        </PagedTableShell>
      </section>
      <section className="flex flex-col overflow-hidden admin-surface">
        <div className="shrink-0 border-b border-slate-100 px-5 py-4 dark:border-slate-800">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            {text('Ανά website', 'By website')}
          </h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {text(
              'Η ίδια εκτίμηση χωρισμένη στην ιστοσελίδα που προκάλεσε την κλήση.',
              'The same estimate broken down by the website that triggered the call.',
            )}
          </p>
        </div>
        <PagedTableShell pagination={sitesPaging.total > 0 ? sitesPaging.pager : null}>
          <Table
            head={[
              text('Πελάτης', 'Customer'),
              'Website',
              text('Κλήσεις', 'Calls'),
              text('Tokens εισόδου', 'Input tokens'),
              text('Tokens εξόδου', 'Output tokens'),
              text('Εκτίμηση', 'Estimate'),
            ]}
          >
            {sitesPaging.rows.map(({ tenant, site }) => (
              <tr key={site.siteId}>
                <Td>{tenant.name}</Td>
                <Td>
                  <span className="font-medium text-slate-900 dark:text-slate-100">
                    {site.deleted
                      ? text('Διαγραμμένο website', 'Deleted website')
                      : hostOf(site.origin)}
                  </span>
                  {site.name ? (
                    <p className="text-xs text-slate-500 dark:text-slate-400">{site.name}</p>
                  ) : null}
                </Td>
                <Td>{countLabel(site.calls, intlLocale)}</Td>
                <Td>{countLabel(site.inputTokens, intlLocale)}</Td>
                <Td>{countLabel(site.outputTokens, intlLocale)}</Td>
                <Td>{cost(site)}</Td>
              </tr>
            ))}
          </Table>
        </PagedTableShell>
      </section>
    </section>
  );
}
